import log, { LoggingLevel } from "../../../core/log.js";
import { deleteKeyFromNestedObject, getValueFromNestedObject, updateNestedObject } from "../../../core/storage/utils.js";
import { isJSONValue, isObject, isUndefined } from "../../../core/utils.js";
const LOG_TAG = "platform.web.Storage";
const DATABASE_NAME = "Glean";
const STORE_NAME = "Main";
var DatabaseTransactionModes;
(function (DatabaseTransactionModes) {
    DatabaseTransactionModes["ReadOnly"] = "readonly";
    DatabaseTransactionModes["ReadWrite"] = "readwrite";
})(DatabaseTransactionModes || (DatabaseTransactionModes = {}));
var IDBWrapper;
(function (IDBWrapper) {
    let db;
    function withDatabase(fn) {
        if (db) {
            return fn();
        }
        return new Promise((resolve, reject) => {
            const openRequest = self.indexedDB.open(DATABASE_NAME);
            openRequest.onerror = () => {
                log(LOG_TAG, ["Unable to open Glean database.", openRequest.error]);
                reject(openRequest.error);
            };
            openRequest.onsuccess = () => {
                db = openRequest.result;
                fn()
                    .then(() => resolve())
                    .catch(e => reject(e));
            };
            openRequest.onupgradeneeded = () => {
                openRequest.result.createObjectStore(STORE_NAME);
            };
        });
    }
    async function withStoredValue(key, fn, mode) {
        await withDatabase(async () => {
            const transaction = db === null || db === void 0 ? void 0 : db.transaction(STORE_NAME, mode);
            if (!transaction) {
                throw new Error("IMPOSSIBLE: Unable to perform database transaction. Database is not initialized.");
            }
            const completed = new Promise((resolve, reject) => {
                transaction.oncomplete = () => resolve();
                transaction.onerror = transaction.onabort = e => reject(e);
            });
            const store = transaction.objectStore(STORE_NAME);
            const storedValue = await new Promise((resolve, reject) => {
                const req = store.get(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            });
            let correctedValue;
            if (isUndefined(storedValue)) {
                correctedValue = {};
            }
            else if (isJSONValue(storedValue) && isObject(storedValue)) {
                correctedValue = storedValue;
            }
            else {
                log(LOG_TAG, ["Unexpected data found in storage. Overwriting.", storedValue], LoggingLevel.Warn);
                correctedValue = {};
            }
            await fn(correctedValue, store);
            await completed;
        });
    }
    IDBWrapper.withStoredValue = withStoredValue;
})(IDBWrapper || (IDBWrapper = {}));
class WebStore {
    constructor(rootKey) {
        this.rootKey = rootKey;
        this.logTag = `${LOG_TAG}.${rootKey}`;
    }
    async get(index = []) {
        let result;
        await IDBWrapper.withStoredValue(this.rootKey, (value) => {
            if (index.length > 0) {
                result = getValueFromNestedObject(value, index);
            }
            else {
                result = Object.keys(value).length === 0 ? undefined : value;
            }
            return Promise.resolve();
        }, DatabaseTransactionModes.ReadOnly);
        return result;
    }
    async update(index, transformFn) {
        await IDBWrapper.withStoredValue(this.rootKey, async (value, store) => {
            await new Promise((resolve, reject) => {
                const updatedObj = updateNestedObject(value, index, transformFn);
                const request = store.put(updatedObj, this.rootKey);
                request.onsuccess = resolve;
                request.onerror = () => reject(request.error);
            });
        }, DatabaseTransactionModes.ReadWrite);
    }
    async delete(index) {
        await IDBWrapper.withStoredValue(this.rootKey, async (value, store) => {
            if (index.length === 0) {
                store.delete(this.rootKey);
            }
            else {
                await new Promise((resolve, reject) => {
                    try {
                        const updatedObj = deleteKeyFromNestedObject(value, index);
                        const request = store.put(updatedObj, this.rootKey);
                        request.onsuccess = () => resolve();
                        request.onerror = () => reject(request.error);
                    }
                    catch (e) {
                        log(this.logTag, [`Error attempting to delete key ${index.toString()} from storage. Ignoring.`, e], LoggingLevel.Warn);
                        resolve();
                    }
                });
            }
        }, DatabaseTransactionModes.ReadWrite);
    }
}
export default WebStore;
